import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Ship, Plane, Train, Truck, MapPin, X } from "lucide-react";
import { API_URL } from "../config/api";

const KIND_ICON = {
  PORT: Ship, AIRPORT: Plane, RAIL: Train, ROAD: Truck, INLAND: MapPin, BORDER: MapPin, PLACE: MapPin,
};
const KIND_STYLE = {
  PORT: "bg-blue-100 text-blue-700", AIRPORT: "bg-purple-100 text-purple-700",
  RAIL: "bg-emerald-100 text-emerald-700", ROAD: "bg-amber-100 text-amber-700",
  INLAND: "bg-teal-100 text-teal-700", BORDER: "bg-rose-100 text-rose-700",
  PLACE: "bg-gray-100 text-gray-600",
};

/**
 * PlaceInput — global place autocomplete backed by /api/places/search
 * (full-world UN/LOCODE + IATA). CMA/Maersk-style dropdown that guarantees a
 * valid code is captured so no rate lookup ever fails on "unknown place".
 *
 * Props:
 *   value        display string (name or code)
 *   onSelect     fn({ code, name, country, country_name, kind, code_type, label })
 *   kind         "sea" | "air" | "inland" | "all"  (default "sea")
 *   placeholder, "data-testid", allowFreeText (default true)
 */
export default function PlaceInput({
  value = "",
  onSelect,
  kind = "sea",
  placeholder = "Search city, port or airport...",
  allowFreeText = true,
  className = "",
  "data-testid": dataTestId,
}) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/places/search?q=${encodeURIComponent(q)}&kind=${kind}&limit=12`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || []);
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [query, kind, token]);

  const choose = useCallback((r) => {
    setQuery(`${r.name} (${r.code})`);
    setOpen(false);
    setResults([]);
    onSelect?.(r);
  }, [onSelect]);

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((p) => Math.min(p + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((p) => Math.max(p - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (idx >= 0) choose(results[idx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={query}
          data-testid={dataTestId}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value); setOpen(true); setIdx(-1);
            if (allowFreeText) onSelect?.({ code: null, name: e.target.value, country: null, kind: "FREETEXT", code_type: null, label: e.target.value, freeText: true });
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          className="w-full pl-10 pr-9 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          autoComplete="off"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(""); setResults([]); onSelect?.({ code: null, name: "", freeText: true }); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-[60] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-auto">
          {loading && results.length === 0 && (
            <div className="px-3 py-2.5 text-sm text-gray-400">Searching…</div>
          )}
          {results.map((r, i) => {
            const Icon = KIND_ICON[r.kind] || MapPin;
            return (
              <div key={`${r.code_type}-${r.code}`} onClick={() => choose(r)}
                data-testid={dataTestId ? `${dataTestId}-opt-${r.code}` : undefined}
                className={`px-3 py-2.5 cursor-pointer flex items-center gap-3 ${i === idx ? "bg-blue-50 border-l-2 border-blue-500" : "hover:bg-gray-50"}`}>
                <Icon className="h-4 w-4 text-gray-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {r.name}
                    <span className="ml-2 text-xs text-blue-600 font-mono bg-blue-50 px-1.5 py-0.5 rounded">{r.code}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{r.country_name || r.country}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${KIND_STYLE[r.kind] || KIND_STYLE.PLACE}`}>{r.kind}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
